import React from "react"
import { Dialog, DialogContent, Box, Stack, InputLabel, useTheme, Divider, FormControl } from "@mui/material"
import CustomAutocomplete from "src/components/CustomAutocomplete"
import { CustomSlider, ValueLabelComponent } from "./CustomSlider"
import { TaxonomyPair, ModelPair, Params } from "./KmControls"
import { CloseModalIconButton } from "src/components/CloseModalIconButton"
import { Language } from "src/utils/useLanguageList"

const SLIDER_LABELS = [
  { label: "Strict Facts", description: "No additions, only database content." },
  { label: "Fact-Based", description: "Primarily factual with slight flexibility." },
  { label: "Balanced", description: "Equal mix of facts and creativity." },
  { label: "Creative", description: "Mostly creative, but still fact-aware." },
  { label: "Unrestricted", description: "Fully open to new ideas." }
]

const exfluencyConfluence = "Exfluency Knowledge Base"

interface KmSettingsModalProps {
  open: boolean
  onClose: () => void
  generate?: boolean
  taxonomies: TaxonomyPair[]
  selectedTaxonomy: TaxonomyPair
  setSelectedTaxonomy: (taxonomy: TaxonomyPair) => void
  lang: Language
  setLang: (lang: Language) => void
  languages: Language[]
  model: ModelPair
  setModel: (model: ModelPair) => void
  models: ModelPair[]
  params?: Params
  temperature?: number
  setTemperature?: (temperature: number) => void
  setParams?: React.Dispatch<React.SetStateAction<Params>>
}

export default function KmSettingsModal({
  open,
  onClose,
  generate,
  taxonomies,
  selectedTaxonomy,
  setSelectedTaxonomy,
  lang,
  setLang,
  languages,
  model,
  setModel,
  models,
  params,
  temperature,
  setTemperature,
  setParams
}: KmSettingsModalProps) {
  const theme = useTheme()
  const handleSelectChange = (selectedTitle: string) => {
    const selectedPrompt = params?.prompts.find((prompt) => prompt.title === selectedTitle)
    if (selectedPrompt) {
      setParams?.((prevParams: Params) => ({
        ...prevParams,
        selectedPrompt: selectedPrompt
      }))
    }
  }

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setTemperature?.(newValue as number)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      fullScreen={true}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: "0",
          backgroundColor: "#E6FBFF"
        }
      }}
    >
      <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ p: theme.spacing(1, 1, 0, 0) }}>
        <CloseModalIconButton onClose={onClose} />
      </Stack>
      <DialogContent
        sx={{
          p: 4,
          position: "relative"
        }}
      >
        <Stack spacing={4} alignItems="center">
          {/* Knowledge Base Selection */}
          {taxonomies && setSelectedTaxonomy && (
            <CustomAutocomplete
              label="Select Knowledge Base"
              data-testid="km-select-taxonomy"
              size="small"
              value={selectedTaxonomy}
              options={taxonomies}
              getOptionLabel={(option) => option.name}
              onChange={(e, val) => val && setSelectedTaxonomy(val as TaxonomyPair)}
              isOptionEqualToValue={(o1, o2) => o1.code === o2.code}
              groupBy={(option) => (option.name === exfluencyConfluence ? "Common" : "Community")}
              disableClearable
            />
          )}

          {/* Output Language */}
          <CustomAutocomplete
            data-testid="km-select-language"
            size="small"
            value={lang}
            options={languages}
            getOptionLabel={(option) => option.name}
            onChange={(e, val) => val && setLang(val as Language)}
            label={"Select output language"}
            isOptionEqualToValue={(o1, o2) => o1.languageId === o2.languageId}
            disableClearable
          />

          {/* Model Selection */}
          {models && setModel && model && (
            <CustomAutocomplete
              data-testid="km-select-model"
              size="small"
              value={model}
              options={models}
              getOptionLabel={(option) => option.name}
              onChange={(e, val) => val && setModel(val as ModelPair)}
              label={"Select Large Language Model"}
              isOptionEqualToValue={(o1, o2) => o1.model === o2.model}
              disableClearable
            />
          )}

          {/* Template Selection for Generate mode */}
          {generate && params && (
            <CustomAutocomplete
              data-testid="km-select-prompt"
              size="small"
              value={params.selectedPrompt.title}
              options={params.prompts.map((prompt) => prompt.title)}
              getOptionLabel={(option) => option}
              onChange={(e, val) => val && handleSelectChange(val as string)}
              label={"Select template"}
              isOptionEqualToValue={(o1, o2) => o1 === o2}
            />
          )}

          {/* Creativity Slider for Generate mode */}
          {generate && temperature !== undefined && (
            <>
              <Divider flexItem sx={{ borderColor: "#AFEFFF" }} />
              <Box sx={{ width: "100%" }}>
                <InputLabel
                  sx={{
                    color: "#01294e",
                    fontSize: "1.125rem",
                    fontWeight: 600,
                    lineHeight: "1.2em",
                    mb: 4
                  }}
                >
                  How creative should I be?
                </InputLabel>
                <CustomSlider
                  data-testid="km-slider"
                  slots={{
                    valueLabel: ValueLabelComponent
                  }}
                  step={1}
                  marks
                  min={1}
                  max={5}
                  value={temperature}
                  onChange={handleSliderChange}
                  valueLabelDisplay={"on"}
                  valueLabelFormat={(value) => SLIDER_LABELS[value - 1].label}
                />
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
